import './Signup.css';

import React, { useState } from 'react';

import {
  autoSignIn,
  confirmSignUp,
  signUp,
} from 'aws-amplify/auth';
import {
  Link,
  useNavigate,
} from 'react-router-dom';

// MUI component imports for consistency
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

const Signup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('signup'); // 'signup', 'confirm', 'success'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    companyName: '',
    confirmationCode: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    setError('');
    
    if (!formData.email) {
      setError('Email is required');
      return false;
    }
    
    if (!formData.password) {
      setError('Password is required');
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    
    // Check for uppercase, lowercase, number, and special character
    const hasUppercase = /[A-Z]/.test(formData.password);
    const hasLowercase = /[a-z]/.test(formData.password);
    const hasNumber = /[0-9]/.test(formData.password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);
    
    if (!hasUppercase || !hasLowercase || !hasNumber || !hasSpecial) {
      setError('Password must include uppercase, lowercase, number, and special character');
      return false;
    }
    
    if (!formData.firstName || !formData.lastName) {
      setError('First and last name are required');
      return false;
    }
    
    return true;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      console.log('Attempting to sign up user:', formData.email);
      
      const { userId, isSignUpComplete, nextStep } = await signUp({
        username: formData.email,
        password: formData.password,
        options: {
          userAttributes: {
            email: formData.email,
            given_name: formData.firstName,
            family_name: formData.lastName,
            'custom:company': formData.companyName || 'Not specified'
          },
          autoSignIn: {
            enabled: true
          }
        }
      });
      
      console.log('Sign up result:', { userId, isSignUpComplete, nextStep });
      
      if (isSignUpComplete) {
        console.log('Sign up complete, attempting auto sign-in');
        try {
          await autoSignIn();
          setStep('success');
          setTimeout(() => {
            navigate('/dashboard');
          }, 1500);
        } catch (autoSignInError) {
          console.log('Auto sign-in failed, redirecting to confirm step', autoSignInError);
          setStep('confirm');
        }
      } else {
        console.log('Moving to confirmation step');
        setStep('confirm');
      }
    } catch (err) {
      console.error('Error signing up:', err);
      
      if (err.name === 'UsernameExistsException') {
        setError('An account with this email already exists. Please sign in or reset your password.');
      } else if (err.name === 'InvalidPasswordException') {
        setError('Password does not meet requirements: ' + err.message);
      } else {
        setError(err.message || 'An error occurred during sign up');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignup = async (e) => {
    e.preventDefault();
    
    if (!formData.confirmationCode) {
      setError('Confirmation code is required');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      console.log('Confirming sign up for:', formData.email);
      
      const { isSignUpComplete } = await confirmSignUp({
        username: formData.email,
        confirmationCode: formData.confirmationCode
      });
      
      console.log('Confirmation result:', { isSignUpComplete });
      
      // Try auto sign-in
      try {
        await autoSignIn();
        setStep('success');
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } catch (autoSignInError) {
        console.log('Auto sign-in not available after confirmation, redirecting to login', autoSignInError);
        setStep('success');
        // Redirect to login after a short delay
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      }
    } catch (err) {
      console.error('Error confirming sign up:', err);
      
      if (err.name === 'CodeMismatchException') {
        setError('Invalid verification code. Please check and try again.');
      } else if (err.name === 'ExpiredCodeException') {
        setError('Verification code has expired. Please request a new one.');
      } else {
        setError(err.message || 'Error confirming your account');
      }
    } finally {
      setLoading(false);
    }
  };

  const resendConfirmationCode = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('Requesting new confirmation code for:', formData.email);
      
      await signUp({
        username: formData.email,
        password: formData.password,
        options: {
          userAttributes: {
            email: formData.email
          }
        }
      });
      
      setError(''); // Clear any previous errors
      alert('A new confirmation code has been sent to your email');
    } catch (err) {
      // If the error is 'User already exists', that's actually what we want
      if (err.name === 'UsernameExistsException') {
        alert('A new confirmation code has been sent to your email');
      } else {
        console.error('Error resending code:', err);
        setError(err.message || 'Error resending confirmation code');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderSignupForm = () => (
    <form onSubmit={handleSignup}>
      <Typography variant="h5" component="h1" gutterBottom>
        Create Your Account
      </Typography>
      
      <Typography variant="body2" color="textSecondary" paragraph>
        Join UtilityAuditPro to start saving on your utility bills.
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <TextField
          label="Email Address"
          variant="outlined"
          fullWidth
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          margin="normal"
          required
        />
        
        <TextField
          label="Password"
          variant="outlined"
          fullWidth
          name="password"
          type="password"
          value={formData.password}
          onChange={handleChange}
          margin="normal"
          required
          helperText="Minimum 8 characters, include uppercase, lowercase, number, and special character"
        />
        
        <TextField
          label="Confirm Password"
          variant="outlined"
          fullWidth
          name="confirmPassword"
          type="password"
          value={formData.confirmPassword}
          onChange={handleChange}
          margin="normal"
          required
        />
      </Box>
      
      <Divider sx={{ my: 3 }} />
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Your Information
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="First Name"
            variant="outlined"
            fullWidth
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            margin="normal"
            required
          />
          
          <TextField
            label="Last Name"
            variant="outlined"
            fullWidth
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            margin="normal"
            required
          />
        </Box>
        
        <TextField
          label="Company Name (Optional)"
          variant="outlined"
          fullWidth
          name="companyName"
          value={formData.companyName}
          onChange={handleChange}
          margin="normal"
        />
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        size="large"
        disabled={loading}
        sx={{ mb: 2 }}
      >
        {loading ? <CircularProgress size={24} /> : 'Create Account'}
      </Button>
      
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Typography variant="body2">
          Already have an account?{' '}
          <Link to="/login" style={{ textDecoration: 'none', color: 'primary' }}>
            Sign In
          </Link>
        </Typography>
      </Box>
    </form>
  );

  const renderConfirmationForm = () => (
    <form onSubmit={handleConfirmSignup}>
      <Typography variant="h5" component="h1" gutterBottom>
        Verify Your Email
      </Typography>
      
      <Typography variant="body2" color="textSecondary" paragraph>
        We've sent a confirmation code to {formData.email}. Please check your email and enter the code below.
      </Typography>
      
      <TextField
        label="Confirmation Code"
        variant="outlined"
        fullWidth
        name="confirmationCode"
        value={formData.confirmationCode}
        onChange={handleChange}
        margin="normal"
        required
      />
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        size="large"
        disabled={loading}
        sx={{ mb: 2 }}
      >
        {loading ? <CircularProgress size={24} /> : 'Verify'}
      </Button>
      
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Button
          variant="text"
          onClick={resendConfirmationCode}
          disabled={loading}
        >
          Resend confirmation code
        </Button>
      </Box>
    </form>
  );

  const renderSuccessMessage = () => (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h5" component="h1" gutterBottom>
        Account Created Successfully!
      </Typography>
      
      <Typography variant="body1" paragraph>
        You are now being redirected to the dashboard...
      </Typography>
      
      <CircularProgress />
    </Box>
  );

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        {step === 'signup' && renderSignupForm()}
        {step === 'confirm' && renderConfirmationForm()}
        {step === 'success' && renderSuccessMessage()}
      </Paper>
    </Container>
  );
};

export default Signup;